/**
 * Firebase Authentication Manager
 * Handles user authentication, token management, and auth state
 * Updated for hosted auth flow
 */

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut, User, Auth } from 'firebase/auth';
import { isAbortLikeError } from './base-api-client.js';

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

class AuthManager {
  isInitialized: boolean;
  private auth: Auth | null;
  private currentUser: User | null;
  private authStateListeners: Array<(user: User | null) => void>;

  constructor() {
    this.isInitialized = false;
    this.auth = null;
    this.currentUser = null;
    this.authStateListeners = [];
  }

  /**
     * Initialize Firebase Auth
     * @param {FirebaseConfig} firebaseConfig - Firebase configuration object
     */
  async initialize(firebaseConfig: FirebaseConfig): Promise<void> {
    try {
      const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
      this.auth = getAuth(app);

      // Wait for Firebase to emit its first auth state callback before returning.
      // Firebase is async here: it reads from IndexedDB/localStorage to restore a
      // persisted session. Setting isInitialized before that callback fires means
      // callers see currentUser = null even when a session exists, causing false
      // unauthenticated redirects.
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('Firebase auth state: initial callback took too long, proceeding');
          if (!this.isInitialized) {
            this.isInitialized = true;
            resolve();
          }
        }, 5000);

        onAuthStateChanged(this.auth!, (user) => {
          this.currentUser = user;

          // Notify all listeners
          this.authStateListeners.forEach(listener => {
            try {
              listener(user);
            } catch (error) {
              console.error('Auth state listener error:', error);
            }
          });

          // Resolve on first callback — this is Firebase's definitive initial state
          // after checking persistence. Subsequent calls are live auth changes.
          if (!this.isInitialized) {
            this.isInitialized = true;
            clearTimeout(timeout);
            resolve();
          }
        });
      });

      console.log('Firebase Auth initialized successfully', {
        currentUser: this.currentUser?.email ?? 'none',
        isAuthenticated: this.currentUser !== null,
      });
    } catch (error) {
      console.error('Failed to initialize Firebase Auth:', error);
      throw error;
    }
  }

  /**
     * Redirect to sign-in page
     * @param {string | null} returnUrl - URL to return to after authentication
     */
  redirectToSignIn(returnUrl: string | null = null): void {
    if (!returnUrl) {
      returnUrl = window.location.href;
    }

    // Construct sign-in page URL with return URL
    const signInUrl = new URL('signin.html', window.location.origin);
    signInUrl.searchParams.set('returnUrl', returnUrl);

    window.location.href = signInUrl.toString();
  }

  /**
     * Redirect to sign-up page
     * @param {string | null} returnUrl - URL to return to after authentication
     */
  redirectToSignUp(returnUrl: string | null = null): void {
    if (!returnUrl) {
      returnUrl = window.location.href;
    }

    // Construct sign-up page URL with return URL
    const signUpUrl = new URL('signup.html', window.location.origin);
    signUpUrl.searchParams.set('returnUrl', returnUrl);

    window.location.href = signUpUrl.toString();
  }

  /**
     * Redirect to auth page (backward compatibility)
     * @param {string | null} returnUrl - URL to return to after authentication
     */
  redirectToAuth(returnUrl: string | null = null): void {
    // Default to sign-in for backward compatibility
    this.redirectToSignIn(returnUrl);
  }

  /**
     * Sign out current user
     */
  async signOut(): Promise<void> {
    if (!this.isInitialized || !this.auth) {
      throw new Error('Auth not initialized');
    }

    try {
      await signOut(this.auth!);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  /**
     * Get current user's ID token
     * @param {boolean} forceRefresh - Force token refresh
     * @returns {Promise<string>} ID token
     */
  async getIdToken(forceRefresh: boolean = false): Promise<string> {
    if (!this.currentUser) {
      throw new Error('No authenticated user');
    }

    try {
      return await this.currentUser.getIdToken(forceRefresh);
    } catch (error) {
      console.error('Get ID token error:', error);
      throw error;
    }
  }

  /**
     * Get current user
     * @returns {User | null} Current user
     */
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
     * Check if user is authenticated
     * @returns {boolean} Authentication status
     */
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /**
     * Add auth state change listener
     * @param {(user: User | null) => void} callback - Callback function
     */
  onAuthStateChanged(callback: (user: User | null) => void): void {
    this.authStateListeners.push(callback);

    // Immediately call with current state
    if (this.isInitialized) {
      callback(this.currentUser);
    }
  }

  /**
     * Remove auth state change listener
     * @param {(user: User | null) => void} callback - Callback function to remove
     */
  removeAuthStateListener(callback: (user: User | null) => void): void {
    const index = this.authStateListeners.indexOf(callback);
    if (index > -1) {
      this.authStateListeners.splice(index, 1);
    }
  }

  /**
     * Make authenticated API call
     * @param {string} url - API endpoint
     * @param {RequestInit} options - Fetch options
     * @returns {Promise<Response>} Fetch response
     */
  async authenticatedFetch(url: string, options: RequestInit = {}, retryOptions: { allowRefreshRetry?: boolean } = {}): Promise<Response> {
    if (!this.isAuthenticated()) {
      throw new Error('User not authenticated');
    }

    try {
      const buildHeaders = async (forceRefresh: boolean): Promise<Record<string, string>> => {
        const token = await this.getIdToken(forceRefresh);
        const headers: Record<string, string> = {
          'Authorization': `Bearer ${token}`,
          ...(options.headers as Record<string, string>)
        };

        if (!headers['Content-Type']) {
          if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
          }
        }

        if ((options.headers as any)?.['Content-Type'] === null) {
          delete headers['Content-Type'];
        }

        return headers;
      };

      const executeFetch = async (forceRefresh: boolean): Promise<Response> => fetch(url, {
        ...options,
        headers: await buildHeaders(forceRefresh)
      });

      const response = await executeFetch(false);
      if (response.status === 401 && retryOptions.allowRefreshRetry !== false) {
        return await executeFetch(true);
      }

      return response;
    } catch (error) {
      if (!isAbortLikeError(error)) {
        console.error('Authenticated fetch error:', error);
      }
      throw error;
    }
  }

  /**
     * Format Firebase auth errors into user-friendly messages
     * @param {any} error - Firebase auth error
     * @returns {Error} Formatted error
     */
  formatAuthError(error: any): Error {
    let message: string;

    switch (error.code) {
      case 'auth/user-not-found':
        message = 'No account found with this email address.';
        break;
      case 'auth/wrong-password':
        message = 'Incorrect password.';
        break;
      case 'auth/email-already-in-use':
        message = 'An account with this email already exists.';
        break;
      case 'auth/weak-password':
        message = 'Password should be at least 6 characters.';
        break;
      case 'auth/invalid-email':
        message = 'Invalid email address.';
        break;
      case 'auth/too-many-requests':
        message = 'Too many failed attempts. Please try again later.';
        break;
      default:
        message = error.message || 'Authentication failed. Please try again.';
    }

    const formattedError = new Error(message);
    (formattedError as any).code = error.code;
    return formattedError;
  }
}

export default AuthManager;
